import { ApplicationState } from "../../ApplicationState.types";
import URN from "../../layout/URN";
import type { SearchResultFixtureWarm } from "../../layout/search-bar-state/SearchBarState.types";
import { getCompetitionByURN } from "../competitions/competition-selectors";
import { getSportByURN } from "../sports/sport-selectors";
import { AvBScore, FootballMatchDuration, TeamDetails } from "../football-fixture/FootballFixture.types";
import { getSportEventByURN } from "./sport-event-selectors";

export type EventViewLabelWarmInputs = {
  sporteventUrn: URN | undefined;
  sportUrn: URN | undefined;
  sportId: number | undefined;
  name: string;
};

/**
 * The scoreboard half of the warm, in Redux's own forms — the writer converts them (`#` prefix, `Date`,
 * `typename`; see `event-view.graphql.ts`). A separate type from `EventViewLabelWarmInputs` because the two
 * halves resolve from different entities and either can be missing on its own.
 */
export type EventViewFixtureWarmInputs = {
  fixtureUrn: URN | undefined;
  sporteventUrn: URN | undefined;
  competitionUrn: URN | undefined;
  competitionName: string | undefined;
  runnerNames: { home: string; away: string } | undefined;
  isAmericanFormat: boolean | undefined;
  scheduledAt: Date | undefined;
  score: AvBScore | undefined;
  firstLegScore: AvBScore | undefined;
  duration: FootballMatchDuration | undefined;
  home: TeamDetails | undefined;
  away: TeamDetails | undefined;
};

/**
 * The `BaseFixture` header's own inputs — the third half. `EventHeader` draws its three rows from the
 * sportevent alone, so this half needs no fixture fields beyond the urn, and `mainMarket` is
 * deliberately absent: nothing the DEFAULT view mode renders reads it. Separate from the football half
 * because a view is backed by exactly one of them.
 */
export type EventViewBaseFixtureWarmInputs = {
  fixtureUrn: URN | undefined;
  sporteventUrn: URN | undefined;
  sporteventName: string | undefined;
  openDate: Date | undefined;
  sportUrn: URN | undefined;
  sportId: number | undefined;
  competitionUrn: URN | undefined;
  competitionName: string | undefined;
};

/**
 * All three halves, resolved together from whichever tiers answer. The halves are `Partial` here, not
 * in the tier helpers' own return types — those stay total, so adding an input field still forces
 * every tier to consider it, while a cache probe may offer only the fields a predicate reads.
 */
export type EventViewWarm = EventViewLabelWarmInputs & {
  fixture?: Partial<EventViewFixtureWarmInputs>;
  baseFixture?: Partial<EventViewBaseFixtureWarmInputs>;
};

/** Any half as a caller can offer it: the cache probe rebuilds only what it tests. */
export type EventViewWarmCandidate = Partial<EventViewLabelWarmInputs> & {
  fixture?: Partial<EventViewFixtureWarmInputs>;
  baseFixture?: Partial<EventViewBaseFixtureWarmInputs>;
};

/** What the label half proves, so its cache write needs no casts to reach its identities. */
export type CompleteEventViewLabelWarm = EventViewLabelWarmInputs & {
  sporteventUrn: URN;
  sportUrn: URN;
  sportId: number;
};

/** The same for the fixture half. */
export type CompleteEventViewFixtureWarm = Partial<EventViewFixtureWarmInputs> & {
  fixtureUrn: URN;
  sporteventUrn: URN;
  runnerNames: { home: string; away: string };
};

/**
 * The same for the `BaseFixture` half. Every field but the competition is required: each one
 * feeds a row `EventHeader` draws, and `renderableFixtureOf` gates that branch on `openDate`, so a
 * half without one paints nothing where the response will paint a header.
 */
export type CompleteEventViewBaseFixtureWarm = Partial<EventViewBaseFixtureWarmInputs> & {
  fixtureUrn: URN;
  sporteventUrn: URN;
  sporteventName: string;
  openDate: Date;
  sportUrn: URN;
  sportId: number;
};

/**
 * The single definition of "warmed": the resolver and every cache write/read go through it. Each half
 * is returned narrowed (cast-free writes) and only when writable alone.
 * Its write fragments and probe are in tbd-shared's `event-view.graphql.ts`, pinned by that file's test.
 */
export function eventViewWarmCompleteness(warm: EventViewWarmCandidate | undefined): {
  label?: CompleteEventViewLabelWarm;
  fixture?: CompleteEventViewFixtureWarm;
  baseFixture?: CompleteEventViewBaseFixtureWarm;
} {
  const { sporteventUrn, sportUrn, sportId, name, fixture, baseFixture: base } = warm ?? {};

  return {
    label:
      sporteventUrn && sportUrn && sportId != null && name ? { sporteventUrn, sportUrn, sportId, name } : undefined,
    fixture:
      fixture?.fixtureUrn && fixture.sporteventUrn && fixture.runnerNames
        ? {
            ...fixture,
            fixtureUrn: fixture.fixtureUrn,
            sporteventUrn: fixture.sporteventUrn,
            runnerNames: fixture.runnerNames,
          }
        : undefined,
    baseFixture:
      base?.fixtureUrn &&
      base.sporteventUrn &&
      base.sporteventName &&
      base.openDate &&
      base.sportUrn &&
      base.sportId != null
        ? {
            ...base,
            fixtureUrn: base.fixtureUrn,
            sporteventUrn: base.sporteventUrn,
            sporteventName: base.sporteventName,
            openDate: base.openDate,
            sportUrn: base.sportUrn,
            sportId: base.sportId,
          }
        : undefined,
  };
}

/** The three search slices type their items separately; tier 3 only ever needs this common subset. */
type WarmSearchItem = {
  urn: URN;
  name: string;
  sportId?: number;
  sporteventUrn?: URN;
  sportUrn?: URN;
  /** Mapped from the plain (never highlighted) `competition` the item has always carried. */
  competition?: string;
  competitionUrn?: URN;
  /** `openDate`, as every search item has always carried it. The `BaseFixture` half's start time. */
  date?: Date;
} & SearchResultFixtureWarm;

/**
 * In a coupon/list the sportevent carries sport only under `competition`, not top-level, so fall back
 * to the competition entity's sport.
 */
function warmInputsFromSportEvent(state: ApplicationState, sporteventURN: URN | undefined) {
  const sportevent = sporteventURN ? getSportEventByURN(state.entities.sportevents, sporteventURN) : undefined;

  if (!sportevent) return undefined;

  const competition = getCompetitionByURN(state.entities.competitions, sportevent.competition);
  const competitionSport = competition && getSportByURN(state.entities.sports, competition.sport);
  // Per field, not per object: a top-level `sport` that is present but missing `urn`/`sportId` must
  // still fall back to the competition's, or the warm resolves incomplete and is dropped.
  const sport = sportevent.sport ?? competitionSport;

  return {
    sporteventUrn: sportevent.urn,
    sportUrn: sport?.urn ?? competitionSport?.urn,
    sportId: sport?.sportId ?? competitionSport?.sportId,
    name: sportevent.name,
  };
}

/**
 * The fixture half, off the normalized football fixture the card/view/search item points at.
 * The competition is the one thing the entity does not hold — no pointer back to its sportevent, so
 * it is joined through the urn the sportevent half resolved. `fallback` is the search item's own copy.
 */
function warmInputsFromFixture(
  state: ApplicationState,
  fixtureURN: URN | undefined,
  sporteventURN: URN | undefined,
  fallback?: Pick<WarmSearchItem, "competition" | "competitionUrn">,
): EventViewFixtureWarmInputs | undefined {
  const fixture = fixtureURN ? state.entities.footballfixtures?.[fixtureURN] : undefined;

  if (!fixture) return undefined;

  const sportevent = sporteventURN ? getSportEventByURN(state.entities.sportevents, sporteventURN) : undefined;
  const competition = getCompetitionByURN(state.entities.competitions, sportevent?.competition);

  return {
    fixtureUrn: fixture.urn,
    sporteventUrn: sportevent?.urn,
    competitionUrn: competition?.urn ?? fallback?.competitionUrn,
    competitionName: competition?.name ?? fallback?.competition,
    runnerNames: fixture.runnerNames,
    isAmericanFormat: fixture.isAmericanFormat,
    scheduledAt: fixture.scheduledAt,
    score: fixture.score,
    firstLegScore: fixture.firstLegScore,
    duration: fixture.duration,
    home: fixture.home,
    away: fixture.away,
  };
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * The `BaseFixture` half. A base fixture has no entity slice of its own — the normalizer inlines it on
 * the card that holds it — so everything but its urn is joined off the sportevent, exactly as the
 * server's `EventHeaderFixture` reads it. `fallback` is the search item's own copy of those fields,
 * for the tier-3 result that has no normalized sportevent behind it.
 */
function baseFixtureWarmInputs(
  state: ApplicationState,
  fixtureURN: URN | undefined,
  sporteventURN: URN | undefined,
  fallback?: Pick<WarmSearchItem, "name" | "competition" | "competitionUrn" | "date" | "sportUrn" | "sportId">,
): EventViewBaseFixtureWarmInputs | undefined {
  if (!fixtureURN) return undefined;

  const sportevent = sporteventURN ? getSportEventByURN(state.entities.sportevents, sporteventURN) : undefined;
  const competition = getCompetitionByURN(state.entities.competitions, sportevent?.competition);
  const competitionSport = competition && getSportByURN(state.entities.sports, competition.sport);
  const sport = sportevent?.sport ?? competitionSport;

  return {
    fixtureUrn: fixtureURN,
    sporteventUrn: sporteventURN,
    sporteventName: sportevent?.name ?? fallback?.name,
    // Redux holds the raw server string; the write needs an instant either way.
    openDate: sportevent?.openDate ? new Date(sportevent.openDate) : fallback?.date,
    sportUrn: sport?.urn ?? fallback?.sportUrn,
    sportId: sport?.sportId ?? fallback?.sportId,
    competitionUrn: competition?.urn ?? fallback?.competitionUrn,
    competitionName: competition?.name ?? fallback?.competition,
  };
}

/**
 * Tier 1: the normalized view entity is a keyed lookup and already carries the sport outright.
 * Returns nothing unless its own sportevent entity resolves — a view entity whose sportevent has not
 * been normalized yet holds `sportId` but no identity and no label, and answering with that would
 * shadow the later tiers that do have both (`??` in the resolver takes the first non-`undefined`).
 */
function fromViewEntity(state: ApplicationState, viewUrn: URN): EventViewWarm | undefined {
  const view = state.layouts.views.event?.[viewUrn];

  if (!view) return undefined;

  const derived = warmInputsFromSportEvent(state, view.sportevent);

  if (!derived) return undefined;

  // Two hops: the view entity has no fixture pointer and its `items` are only `{ urn, typename }`, so
  // the FixtureCard item's urn keys the card slice that does hold one.
  const fixtureCardUrn = view.items?.find((item) => item.typename === "FixtureCard")?.urn;
  const cardFixture = fixtureCardUrn ? state.layouts.cards.fixtures?.[fixtureCardUrn]?.fixture : undefined;

  // `FixtureCard.fixture` is `URN | BaseFixture`: a urn keys the football entity, while an inline
  // object IS the base fixture and is the only place its urn appears in the store.
  const inlineBaseFixture = isObject(cardFixture) && cardFixture.typename === "BaseFixture" ? cardFixture : undefined;

  // The view's own `sportId` only fills a gap; the sportevent's is the same value from the same payload.
  return {
    ...derived,
    sportId: derived.sportId ?? view.sportId,
    fixture: warmInputsFromFixture(state, typeof cardFixture === "string" ? cardFixture : undefined, view.sportevent),
    baseFixture: baseFixtureWarmInputs(state, inlineBaseFixture?.urn, view.sportevent),
  };
}

/**
 * Tier 2: name-agnostic scan of the normalized cards. Six card types carry `(viewLink, sportevent)` in
 * one object under four field spellings, so match on shape instead: a card whose values include an
 * object pointing at `viewUrn`, and a string value that keys a known sportevent. Slices are not
 * uniform (`mybets` holds scalars beside cards), so every level is shape-checked.
 */
function fromCardScan(state: ApplicationState, viewUrn: URN): EventViewWarm | undefined {
  const sportevents = state.entities.sportevents;
  const fixtures = state.entities.footballfixtures ?? {};
  const matched = new Set<URN>();
  const matchedFixtures = new Set<URN>();

  // hasOwnProperty, not `in`: `in` walks the prototype chain, so a card string like "toString"
  // would match and resolve to `Function.prototype.name` as the label.
  const ownKeyOf =
    (entities: Record<string, unknown>) =>
    (value: unknown): value is URN =>
      typeof value === "string" && Object.prototype.hasOwnProperty.call(entities, value);

  Object.values(state.layouts.cards).forEach((slice) => {
    if (!isObject(slice)) return;

    Object.values(slice).forEach((card) => {
      if (!isObject(card)) return;

      const values = Object.values(card);

      if (!values.some((value) => isObject(value) && value.viewUrn === viewUrn)) return;

      const sporteventURN = values.find(ownKeyOf(sportevents));
      // All five card types that carry the pair carry a fixture urn beside it, under the one spelling
      // `fixture` — but matched by shape like the sportevent, so no card type is named here either.
      const fixtureURN = values.find(ownKeyOf(fixtures));

      if (sporteventURN) matched.add(sporteventURN);
      if (fixtureURN) matchedFixtures.add(fixtureURN);
    });
  });

  // Ambiguity falls back to the query rather than latching a guessed sport: a card can hold several
  // viewLinks (`tabViewLink`, `marketGraphsViewLink`) pointing away from its own sportevent. Same
  // discipline for the fixture, or an ambiguous page latches another match's scoreboard.
  if (matched.size !== 1) return undefined;

  const derived = warmInputsFromSportEvent(state, [...matched][0]);

  if (!derived) return undefined;

  return {
    ...derived,
    fixture:
      matchedFixtures.size === 1
        ? warmInputsFromFixture(state, [...matchedFixtures][0], derived.sporteventUrn)
        : undefined,
  };
}

/**
 * Tier 3: search results are the one source with no normalized sportevent behind them. Three slices,
 * one per surface — the `searchbar` card, the urn-less desktop sidebar, and the browse view's own tab —
 * all written from the same mapper, so the shape is identical in each.
 */
function fromSearchResults(state: ApplicationState, viewUrn: URN): EventViewWarm | undefined {
  const itemLists: (WarmSearchItem[] | undefined)[] = [
    ...Object.values(state.layouts.searchBarState?.search ?? {}).map((search) => search?.result?.items),
    state.layouts.searchBar?.search?.result?.items,
    ...Object.values(state.layouts.views.browse ?? {}).map((browse) => browse?.search?.result?.items),
  ];

  for (const items of itemLists) {
    const item = items?.find((candidate) => candidate.urn === viewUrn);

    if (item) {
      return {
        sporteventUrn: item.sporteventUrn,
        sportUrn: item.sportUrn,
        sportId: item.sportId,
        name: item.name,
        // The item names which half it can warm, so a base fixture is never warmed as a scoreboard
        // (`writeEventViewFixtureWarm` writes a `FootballFixture` whatever it is handed).
        fixture: item.fixtureTypename === "FootballFixture" ? fixtureWarmFromSearchItem(state, item) : undefined,
        baseFixture:
          item.fixtureTypename === "BaseFixture"
            ? baseFixtureWarmInputs(state, item.fixtureUrn, item.sporteventUrn, item)
            : undefined,
      };
    }
  }

  return undefined;
}

/**
 * A revisited search result may already have a normalized entity, so prefer it; otherwise the item's
 * own fields feed every row the scoreboard draws, so the frame reserves its settled height.
 * `isAmericanFormat`, `home` and `away` are not on the item: the writer's placeholders fill them, and
 * never over a value the entity already holds.
 */
function fixtureWarmFromSearchItem(
  state: ApplicationState,
  item: WarmSearchItem,
): EventViewFixtureWarmInputs | undefined {
  if (!item.fixtureUrn) return undefined;

  return (
    warmInputsFromFixture(state, item.fixtureUrn, item.sporteventUrn, item) ?? {
      fixtureUrn: item.fixtureUrn,
      sporteventUrn: item.sporteventUrn,
      competitionUrn: item.competitionUrn,
      competitionName: item.competition,
      runnerNames: item.runnerNames,
      isAmericanFormat: undefined,
      scheduledAt: item.scheduledAt,
      score: item.score,
      firstLegScore: undefined,
      duration: item.duration,
      home: undefined,
      away: undefined,
    }
  );
}

/**
 * The values the EventView header warm needs, resolved from the urn `<EventView>` already holds.
 * Three tiers; `undefined` means the header falls back to its own query. The halves resolve
 * independently — first *writable* label and first writable fixture of either kind, whichever tiers
 * those are; all keyed on the same `viewUrn`. Tiers: `docs/specs/event-view-apollo-header-migration.md` §13.4.3.
 */
export function resolveEventViewWarm(state: ApplicationState, viewUrn: URN | undefined): EventViewWarm | undefined {
  if (!viewUrn) return undefined;

  let answered: EventViewWarm | undefined;
  let label: EventViewWarm | undefined;
  let fixture: CompleteEventViewFixtureWarm | undefined;
  let baseFixture: CompleteEventViewBaseFixtureWarm | undefined;

  // A writable half wins over an earlier tier's unwritable one: only a complete half is written, so a
  // partial must not deny a later tier the chance to supply every field.
  for (const tier of [fromViewEntity, fromCardScan, fromSearchResults]) {
    const warm = tier(state, viewUrn);

    if (!warm) continue;

    const complete = eventViewWarmCompleteness(warm);

    answered ??= warm;
    if (!label && complete.label) label = warm;
    if (!fixture && complete.fixture) fixture = complete.fixture;
    if (!baseFixture && complete.baseFixture) baseFixture = complete.baseFixture;
    if (label && (fixture || baseFixture)) break;
  }

  const winner = label ?? answered;

  return (
    winner && {
      ...winner,
      fixture: fixture ?? winner.fixture,
      baseFixture: baseFixture ?? winner.baseFixture,
    }
  );
}
